import { useState } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { fadeIn } from '@/shared/motion/motionPresets';
import { useBulkReferralCommit } from '../../../hooks/useBulkReferralCommit';
import { BulkUploadStage as UploadStage } from './BulkUploadStage';
import { ReconciliationStage } from './ReconciliationStage';
import { SuccessStage } from './SuccessStage';
import type { BulkReferralResponseDto } from '../../../types/bulk-referral.type';

export type BulkUploadStage = 'upload' | 'reconciliation' | 'success';

type BulkUploadSurfaceProps = {
  onBack: () => void;
};

const STAGE_SUBTITLE: Record<BulkUploadStage, string> = {
  upload: 'Import referrals recorded outside the platform from a CSV.',
  reconciliation: 'Nothing is saved until you confirm.',
  success: 'Import complete.',
};

/**
 * Replaces the Referrals tab content while the bulk-upload flow is active.
 * Stage bodies (upload / reconciliation / success) land in issues 18–21;
 * the commit wiring below (same in-memory `File` re-sent with
 * `dryRun: false`, staying on reconciliation with an inline error on
 * failure) is issue 22 (spec §7.1).
 */
export function BulkUploadSurface({ onBack }: BulkUploadSurfaceProps) {
  const [stage, setStage] = useState<BulkUploadStage>('upload');
  const [reconciliationData, setReconciliationData] = useState<BulkReferralResponseDto | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const commit = useBulkReferralCommit();

  const handleStageChange = (newStage: BulkUploadStage, data?: BulkReferralResponseDto) => {
    setStage(newStage);
    if (data) setReconciliationData(data);
  };

  const handleUploadAnother = () => {
    setSelectedFile(null);
    setReconciliationData(null);
    commit.reset();
    setStage('upload');
  };

  const handleImport = () => {
    if (!selectedFile) return;
    commit.mutate(
      { file: selectedFile, dryRun: false },
      { onSuccess: (data) => handleStageChange('success', data) },
    );
  };

  const renderStage = () => {
    switch (stage) {
      case 'upload':
        return <UploadStage onStageChange={handleStageChange} onFileSelected={setSelectedFile} />;
      case 'reconciliation':
        if (!reconciliationData) return null;
        return (
          <ReconciliationStage
            data={reconciliationData}
            onImport={handleImport}
            isImporting={commit.isPending}
            commitError={commit.isError ? commit.error.message : null}
          />
        );
      case 'success':
        if (!reconciliationData) return null;
        return (
          <SuccessStage
            summary={reconciliationData.summary}
            failureCsv={reconciliationData.failureCsv}
            fileName={selectedFile?.name}
            onUploadAnother={handleUploadAnother}
          />
        );
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      {/*
        AdminPortal is min-h-screen in normal document flow, so h-full here
        resolves to auto: the document scrolls, sticky/absolute children unstick.
        Bounding to the viewport (minus admin header + tabs) keeps scroll inside.
        On a short screen min-h wins: the page scrolls instead of crushing the table.
        ponytail: 13rem is measured against today's admin chrome. If the header or
        tab bar changes height, swap this for a ResizeObserver on the top offset.
      */}
      <div className="flex h-[calc(100vh-13rem)] min-h-[38rem] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Back to referrals"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Bulk upload manual referrals</h2>
            <p className="text-sm text-gray-600">{STAGE_SUBTITLE[stage]}</p>
          </div>
        </div>
        {/*
          min-h-0 lets this flex item shrink to its allotted space instead of
          growing to fit content — required for a stage's own inner region
          (e.g. the reconciliation table) to scroll instead of the document.
        */}
        <motion.div key={stage} {...fadeIn} className="relative flex min-h-0 flex-1 flex-col overflow-auto">
          {renderStage()}
        </motion.div>
      </div>
    </MotionConfig>
  );
}
